// ─────────────────────────────────────────────────────────────────────────────
// atmosphere — full-screen homepage atmosphere constants (Chunk 5, desktop).
//
// Mirrors the iOS rexyAtmosphere.ts values byte-for-byte. CSS context (no Skia):
// the homepage crossfades COOL → WARM by bgWarmth = smoothstep(0.45, 0.95, warmth)
// so idle / listening / user-speaking stay cool and only thinking / agent-speaking
// turn warm. Colours are [r,g,b,a] (rgb 0-255, a 0-1).
// ─────────────────────────────────────────────────────────────────────────────

export type RGBA = [number, number, number, number];

// Deep-navy root fallback — paints before the gradient mounts (no flash). Also the
// RexyHomepage root base (D3).
export const ATMOSPHERE_FALLBACK_BG = '#050818';

// Radial atmosphere geometry (relative to the viewport), mirrors iOS.
export const ATMOSPHERE_CENTER_X = 0.5;
export const ATMOSPHERE_CENTER_Y = 0.34;
export const ATMOSPHERE_RADIUS = 0.85;
export const ATMOSPHERE_STOPS = [0, 0.42, 1.0] as const;

export const BASE_COOL: RGBA = [5, 8, 24, 1.0];
export const COOL_CENTER: RGBA = [22, 116, 138, 0.62]; // D3: stronger luminous teal (iOS parity)
export const COOL_MID: RGBA = [8, 45, 70, 0.72]; // D3
export const COOL_OUTER: RGBA = [5, 8, 24, 1.0];

export const BASE_WARM: RGBA = [14, 10, 12, 1.0];
export const WARM_CENTER: RGBA = [95, 72, 44, 0.52];
export const WARM_MID: RGBA = [43, 30, 21, 0.76];
export const WARM_OUTER: RGBA = [8, 7, 14, 1.0];

export function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

export function rgba(c: RGBA): string {
  return `rgba(${c[0]},${c[1]},${c[2]},${c[3]})`;
}

// Lerp two RGBA tuples by k (rgb rounded, alpha not).
export function mix(a: RGBA, b: RGBA, k: number): RGBA {
  return [
    Math.round(a[0] + (b[0] - a[0]) * k),
    Math.round(a[1] + (b[1] - a[1]) * k),
    Math.round(a[2] + (b[2] - a[2]) * k),
    a[3] + (b[3] - a[3]) * k,
  ];
}
